/* Casca da aplicacao: sidebar, barra de topo, paleta de comandos, rodape de
 * proveniencia.
 *
 * Sidebar e nao abas. As tres abas antigas viraram nove destinos, e aba
 * horizontal quebra passando de ~5 — vira menu "mais", que le como inacabado.
 * As tres faixas rotuladas (Analise / Ontologia / Confianca) explicam a
 * estrutura do produto, e sobra lugar fixo para o carimbo do build. */

import { type ReactNode, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Meta, UserRow } from "../lib/api";
import { compact, fmtDateTime, shortId } from "../lib/format";
import { CommandPalette } from "./CommandPalette";
import { ThemeToggle } from "./ThemeToggle";

const NAV = [
  {
    band: "Análise",
    items: [
      { to: "/visao-geral", label: "Visão geral", icon: "◧" },
      { to: "/categorias", label: "Por categoria", icon: "▦" },
      { to: "/usuarios", label: "Por usuário", icon: "◔", needsUser: true },
      { to: "/relatorios", label: "Relatórios", icon: "▧" },
      { to: "/margin-finder", label: "Margin Finder", icon: "◑" },
      { to: "/user-statistics", label: "User statistics", icon: "▥" },
    ],
  },
  {
    band: "Ontologia",
    items: [
      { to: "/ontologia", label: "Descrições", icon: "☰", needsUser: true },
      { to: "/grafo", label: "Grafo", icon: "◈", needsUser: true },
    ],
  },
  {
    band: "Confiança",
    items: [
      { to: "/qualidade", label: "Qualidade", icon: "✓" },
      { to: "/fontes", label: "Fontes", icon: "▤" },
    ],
  },
];

export function useUsers() {
  return useQuery({
    queryKey: ["users"],
    queryFn: () => api<UserRow[]>("/api/users"),
    staleTime: 5 * 60_000,
  });
}

export function useMeta() {
  return useQuery({
    queryKey: ["meta"],
    queryFn: () => api<Meta>("/api/meta"),
    staleTime: 5 * 60_000,
  });
}

/** Usuario ativo: da rota quando existe, senao o de maior massa. */
export function useActiveUser(): string | undefined {
  const { userId } = useParams();
  const { data } = useUsers();
  if (userId) return userId;
  return data?.find((u) => !u.is_aggregate)?.user_id;
}

export function Shell() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { data: meta } = useMeta();
  const { data: users } = useUsers();
  const active = useActiveUser();
  const navigate = useNavigate();
  const client = useQueryClient();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const current = users?.find((u) => u.user_id === active);

  return (
    <div className="shell">
      <a className="skip" href="#conteudo">Pular para o conteúdo</a>

      <nav className="sidebar" aria-label="Seções">
        <div className="brand">
          <strong>Classificador</strong>
          <span>categorização de transações</span>
        </div>

        {NAV.map((band) => (
          <div className="navband" key={band.band}>
            <h2>{band.band}</h2>
            {band.items.map((item) => (
              <NavLink key={item.to}
                       to={item.needsUser && active ? `${item.to}/${active}` : item.to}>
                <span className="ico" aria-hidden>{item.icon}</span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>
        ))}

        <div className="navband">
          <NavLink to="/guia">
            <span className="ico" aria-hidden>◇</span><span>Guia</span>
          </NavLink>
        </div>

        {meta && (
          <div className="provenance">
            v{meta.version} · <code>{meta.git_sha.slice(0, 7)}</code><br />
            dados {fmtDateTime(meta.generated_at)}<br />
            corte {meta.cut_ym}
          </div>
        )}
      </nav>

      <div>
        <header className="topbar">
          <button className="userpick" onClick={() => setPaletteOpen(true)}
                  aria-label="Trocar de usuário ou navegar">
            <span aria-hidden>◔</span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {current
                ? current.is_aggregate
                  ? "Todos os usuários"
                  : `${shortId(current.user_id)}… · ${current.months} meses · R$ ${compact(current.total)}`
                : "Selecionar usuário"}
            </span>
            <kbd>⌘K</kbd>
          </button>
          <div className="spacer" />
          <ThemeToggle />
          <button className="btn" onClick={async () => {
            await api("/api/auth/logout", { method: "POST" });
            // O simétrico do login: sem limpar, o cache continua dizendo
            // `authenticated:true` e a próxima rota protegida aparece por um instante
            // antes de expulsar. Limpa tudo, e não só a sessão: o resto do cache são
            // os dados de quem acabou de sair.
            client.clear();
            navigate("/login");
          }}>sair</button>
        </header>

        {meta?.drift && (
          <div style={{ padding: "16px 26px 0" }}>
            <div className="banner" role="status">
              <span aria-hidden>⚠</span>
              <span>
                A fonte mudou desde que estes números foram gerados{" "}
                ({fmtDateTime(meta.generated_at)}). Atualize em{" "}
                <NavLink to="/fontes">Fontes</NavLink>.
              </span>
            </div>
          </div>
        )}

        <main id="conteudo">
          <Outlet />
        </main>
      </div>

      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} />}
    </div>
  );
}

export function Provider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
